/**
 * Internal admin (CVSI-staff) API client + DTOs (Doc §6). Cross-tenant; authenticates with a staff
 * bearer token and an `X-CVSI-Actor` header naming the operator (for the audit trail). Mirrors the
 * `/internal/*` routes served by the `cvsiInternal` function.
 */

import { normalizeCvsiError, CvsiError } from './errors';
import type { ApiKeySummary, ApiKeyWithSecret, ListResult, ReviewDecision, ReviewQueueItem, SupportStatus, SupportTicket, TenantStatus, Vertical, SafetyForecastReport, SafetyAnomalyReport } from './types';

export type BillingMode = 'manual_contract' | 'stripe';

export interface TenantSummary {
  demo?: boolean;
  syntheticTestRuns?: string[];
  tenantId: string;
  tenantCode?: string | null;
  name: string;
  email: string;
  vertical: Vertical;
  status: TenantStatus;
  emailVerified: boolean;
  billingPlanId: string;
  createdAt: string | null;
}

export interface LiveAccessChecklist {
  tenantId: string;
  emailVerified: boolean;
  pilotAgreementSigned: boolean;
  dpaSigned: boolean;
  billingModeSelected: BillingMode | null;
  securityReviewComplete: boolean;
  productionPolicySelected: boolean;
  webhookConfigured: boolean;
  supportContactsDefined: boolean;
  rateLimitsConfirmed: boolean;
  approvedBy: string | null;
  approvedAt: string | null;
}

export interface LiveAccessState {
  checklist: LiveAccessChecklist;
  canIssueLiveKey: boolean;
  outstanding: string[];
}

export interface TenantDetail extends TenantSummary {
  rateLimitPerMin: number;
  ssoConfigured: boolean;
  webhookEndpoints: number;
  liveAccess: LiveAccessState;
}

export interface SandboxTenantProvisionResult {
  tenantId: string;
  tenantCode: string;
  name: string;
  vertical: Vertical;
  status: 'sandbox';
  demo: true;
  runId: string;
  fixtureNamespace: string;
  testApiKey: string;
  externalEgress: { webhooks: false; email: false; billing: false };
  message: string;
}

export interface ApprovalItem extends TenantSummary {
  canIssueLiveKey: boolean;
  outstanding: string[];
}

export interface AuditVerifyResult {
  tenantId: string;
  chain: { ok: boolean; checked: number; brokenAtSeq: number | null };
  manifest: AuditManifest | null;
  recentManifests: AuditManifest[];
}

export interface AuditManifest {
  tenantId: string;
  date: string;
  startSeq: number;
  endSeq: number;
  firstHash: string;
  lastHash: string;
  eventCount: number;
  manifestHash: string;
  signedAt: string;
  signatureVersion: string;
}

export interface SecurityEvent {
  tenantId: string;
  action: string;
  actorId: string;
  target?: string;
  at: string;
  seq: number;
}

export interface LegalHold {
  id: string;
  tenantId: string;
  scope: string | null;
  reason: string;
  status: 'active' | 'release_pending' | 'released';
  createdBy: string;
  createdAt: string | null;
  /** Set once release has been requested (dual control step 1) — still on hold until a
   *  DIFFERENT staff member approves via approveLegalHoldRelease (step 2). */
  releaseRequestedBy: string | null;
  releaseRequestedAt: string | null;
  releaseReason: string | null;
  releasedBy: string | null;
  releasedAt: string | null;
}

export interface ReleaseReadiness {
  tenants: { total: number; byStatus: Record<string, number>; pendingApprovals: number };
  health: Record<string, unknown>;
  gates: Record<string, boolean>;
}

export interface SystemHealthHistoryPoint {
  at: string | null;
  status: string;
  apiErrorRate: number | null;
  verificationFailRate: number | null;
}

/** Trend series backing System Health's chart — see monitorHealth.ts, which starts writing this
 *  history once the feature ships, so a fresh environment will show a short series at first. */
export interface SystemHealthHistory {
  days: number;
  count: number;
  points: SystemHealthHistoryPoint[];
}

export type StaffRole = 'super_admin' | 'support' | 'billing' | 'security' | 'viewer';

export interface StaffWhoami {
  actorId: string;
  role: StaffRole;
  areas: string[];
}

/** True when a staff `areas` list grants `area` ('*' = super_admin grants everything). */
export function staffCan(areas: string[] | undefined, area: string): boolean {
  if (!areas) return false;
  return areas.includes('*') || areas.includes(area);
}

export type ReasonCode =
  | 'customer_requested' | 'security_incident' | 'payment_failure' | 'contract_change'
  | 'policy_violation' | 'pilot_reset' | 'internal_test' | 'legal_hold' | 'other';

export const REASON_CODES: ReasonCode[] = [
  'customer_requested', 'security_incident', 'payment_failure', 'contract_change',
  'policy_violation', 'pilot_reset', 'internal_test', 'legal_hold', 'other',
];

export interface CvsiInternalClientOptions {
  baseUrl: string;
  /** Returns the staff bearer token. */
  tokenProvider: () => Promise<string>;
  /** Operator identity recorded in the audit trail (e.g. email). */
  actorId: string;
}

export class CvsiInternalClient {
  private readonly baseUrl: string;
  private readonly tokenProvider: () => Promise<string>;
  private readonly actorId: string;

  constructor(opts: CvsiInternalClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, '');
    this.tokenProvider = opts.tokenProvider;
    this.actorId = opts.actorId;
  }

  get apiBaseUrl() { return this.baseUrl; }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    let res: Response;
    try {
      const token = await this.tokenProvider();
      res = await fetch(`${this.baseUrl}${path}`, {
        ...init,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-CVSI-Actor': this.actorId,
          ...(init.headers ?? {}),
        },
      });
    } catch (err) {
      throw new CvsiError('network', 0, err instanceof Error ? err.message : 'Network request failed');
    }
    if (!res.ok) throw await normalizeCvsiError(res);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  private post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'POST', body: body == null ? undefined : JSON.stringify(body) });
  }

  whoami() { return this.request<StaffWhoami>('/internal/whoami'); }
  listTenants(status?: string) {
    return this.request<ListResult<TenantSummary>>(`/internal/tenants${status ? `?status=${status}` : ''}`);
  }
  provisionSandboxTenant(body: { name: string; tenantCode?: string; email: string; vertical: Vertical; timeZone: string; reasonText: string }) {
    return this.post<SandboxTenantProvisionResult>('/internal/demo-tenants', body);
  }
  updateTenantCode(id: string, tenantCode: string, reasonText?: string) {
    return this.request<{ tenantId: string; tenantCode: string }>(`/internal/tenants/${id}/tenant-code`, { method: 'PUT', body: JSON.stringify({ tenantCode, reasonText }) });
  }
  getTenant(id: string) { return this.request<TenantDetail>(`/internal/tenants/${id}`); }
  getTenantSafetyIntelligence(id: string) { return this.request<{ tenant: TenantSummary; forecast: SafetyForecastReport; anomalies: SafetyAnomalyReport }>(`/internal/tenants/${id}/safety-intelligence`); }
  listApprovals() { return this.request<ListResult<ApprovalItem>>('/internal/approvals'); }
  updateLiveAccess(id: string, patch: Partial<LiveAccessChecklist>) { return this.request<LiveAccessState>(`/internal/tenants/${id}/live-access`, { method: 'PUT', body: JSON.stringify(patch) }); }
  approveLiveAccess(id: string, reasonCode: ReasonCode, reasonText?: string) { return this.post<{ tenantId: string; status: string }>(`/internal/tenants/${id}/approve`, { reasonCode, reasonText }); }
  listTenantKeys(id: string) { return this.request<{ keys: ApiKeySummary[] }>(`/internal/tenants/${id}/keys`); }
  getTenantKeyTelemetry(id: string, keyId: string) { return this.request<import('./types').ApiKeyTelemetry>(`/internal/tenants/${id}/keys/${encodeURIComponent(keyId)}/telemetry`); }
  issueTenantKey(id: string, mode: 'live' | 'test', reasonCode: ReasonCode, reasonText?: string) {
    return this.post<ApiKeyWithSecret>(`/internal/tenants/${id}/keys`, { mode, reasonCode, reasonText });
  }
  suspendTenant(id: string, reasonCode: ReasonCode, reasonText?: string) { return this.post<{ tenantId: string; status: string }>(`/internal/tenants/${id}/suspend`, { reasonCode, reasonText }); }
  reactivateTenant(id: string, reasonCode: ReasonCode, reasonText?: string) { return this.post<{ tenantId: string; status: string }>(`/internal/tenants/${id}/reactivate`, { reasonCode, reasonText }); }
  supportQueue(status?: string) { return this.request<ListResult<SupportTicket>>(`/internal/support/queue${status ? `?status=${status}` : ''}`); }
  getSupportTicket(id: string) { return this.request<SupportTicket>(`/internal/support/tickets/${id}`); }
  addSupportMessage(id: string, body: string, internal = false) { return this.post<{ id: string; added: boolean }>(`/internal/support/tickets/${id}/messages`, { body, internal }); }
  setSupportStatus(id: string, status: SupportStatus) { return this.post<{ id: string; status: SupportStatus }>(`/internal/support/tickets/${id}/status`, { status }); }
  assignSupportTicket(id: string, assignedTo?: string) { return this.post<{ id: string; assignedTo: string; status: SupportStatus }>(`/internal/support/tickets/${id}/assign`, { assignedTo }); }
  verifyAudit(id: string, date?: string) { return this.request<AuditVerifyResult>(`/internal/tenants/${id}/audit${date ? `?date=${date}` : ''}`); }
  securityEvents() { return this.request<ListResult<SecurityEvent>>('/internal/security-events'); }
  releaseReadiness() { return this.request<ReleaseReadiness>('/internal/release-readiness'); }
  systemHealth() { return this.request<Record<string, unknown>>('/internal/system-health'); }
  systemHealthHistory(days = 7) { return this.request<SystemHealthHistory>(`/internal/system-health-history?days=${days}`); }
  createTenantUser(id: string, body: { email: string; password: string; displayName?: string; role: string }) {
    return this.post<{ tenantId: string; email: string; displayName: string | null; role: string; status: string }>(`/internal/tenants/${id}/users`, body);
  }
  createStaffUser(body: { email: string; password: string; displayName?: string; role: StaffRole }) {
    return this.post<{ email: string; displayName: string | null; role: StaffRole; status: string }>('/internal/staff-users', body);
  }
  listLegalHolds(id: string) { return this.request<ListResult<LegalHold>>(`/internal/tenants/${id}/legal-holds`); }
  placeLegalHold(id: string, reasonCode: ReasonCode, reasonText?: string, scope?: string) { return this.post<LegalHold>(`/internal/tenants/${id}/legal-holds`, { reasonCode, reasonText, scope }); }
  /** Dual-control step 1: requests release. Hold stays in effect (status: release_pending). */
  releaseLegalHold(holdId: string, reasonCode: ReasonCode, reasonText?: string) { return this.post<{ id: string; status: string }>(`/internal/legal-holds/${holdId}/release`, { reasonCode, reasonText }); }
  /** Dual-control step 2: a DIFFERENT staff member than the requester must call this to actually
   *  release the hold — the backend rejects it if the same actor tries both steps. */
  approveLegalHoldRelease(holdId: string, reasonCode: ReasonCode, reasonText?: string) { return this.post<{ id: string; status: string }>(`/internal/legal-holds/${holdId}/approve-release`, { reasonCode, reasonText }); }
  listReviewQueue(id: string, limit?: number) {
    return this.request<ListResult<ReviewQueueItem>>(`/internal/tenants/${id}/review-queue${limit ? `?limit=${limit}` : ''}`);
  }
  submitReviewDecision(id: string, eventId: string, decision: ReviewDecision, reasonCode: ReasonCode, reasonText: string) {
    return this.post<{ reviewId: string; eventId: string; decision: ReviewDecision }>(
      `/internal/tenants/${id}/review-queue/${eventId}/decision`, { decision, reasonCode, reasonText },
    );
  }
}
